/**
 * Hook para gerenciar transferência de conversas entre agentes
 * 
 * Fornece funções para transferir, aceitar, rejeitar e listar
 * transferências pendentes de uma conversa
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

// Interface para agente disponível para transferência
export interface TransferableAgent {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
  is_owner: boolean;
}

// Interface para transferência
export interface ConversationTransfer {
  id: string;
  conversation_id: string;
  from_agent_id: string | null;
  to_agent_id: string | null;
  from_inbox_id: string | null;
  to_inbox_id: string | null;
  note: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  created_at: string;
  responded_at: string | null;
  // Dados populados via join
  from_agent?: {
    full_name: string | null;
    avatar_url: string | null;
  };
  to_agent?: {
    full_name: string | null;
    avatar_url: string | null;
  };
}

export function useConversationTransfer() {
  const { currentTenant, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<TransferableAgent[]>([]);

  /**
   * Busca agentes disponíveis para transferência (exceto o usuário atual)
   */
  const fetchAvailableAgents = useCallback(async () => {
    if (!currentTenant?.id || !user?.id) return [];

    setLoading(true);
    try {
      // v2.0.0: Busca membros via RPC server-side (cache compartilhado pelo React Query)
      const { data: members, error: membersError } = await (supabase.rpc as any)(
        'get_tenant_members_safe',
        { p_tenant_hint: currentTenant.id, p_only_active: false }
      );

      if (membersError) throw membersError;

      // Mapeia para o formato esperado (RPC já traz profile join)
      const formattedAgents: TransferableAgent[] = (members || []).map((m: any) => ({
        id: m.user_id, // Usa user_id como id (não há mais tenant_members.id no payload)
        user_id: m.user_id,
        full_name: m.full_name || null,
        email: m.email || '',
        avatar_url: m.avatar_url || null,
        is_owner: m.is_owner || false,
      }));

      setAgents(formattedAgents);
      return formattedAgents;
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
      toast.error('Erro ao carregar lista de agentes');
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentTenant?.id, user?.id]);

  /**
   * Transfere uma conversa para outro agente
   */
  const transferToAgent = useCallback(async (
    conversationId: string,
    toAgentId: string,
   toAgentName: string,
   note?: string,
   // Dados para broadcast webchat (mensagem de sistema)
   webchatData?: {
     inboxId: string;
   }
  ): Promise<boolean> => {
    if (!user?.id) {
      toast.error('Usuário não autenticado');
      return false;
    }

    setLoading(true);
    try {
      // 1. Cria registro de transferência
      const { error: transferError } = await supabase
        .from('conversation_transfers')
        .insert({
          conversation_id: conversationId,
          from_agent_id: user.id,
          to_agent_id: toAgentId,
          note: note || null,
          status: 'pending',
        });

      if (transferError) throw transferError;

      // 2. Atualiza assigned_to da conversa e pausa o bot automaticamente
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          assigned_to: toAgentId,
          status: 'open', // Garante que a conversa está aberta
          // Pausa bot automaticamente ao transferir para agente manualmente
          bot_paused: true,
          bot_paused_at: new Date().toISOString(),
          bot_paused_by: user.id,
        })
        .eq('id', conversationId);

      if (updateError) throw updateError;

     // ========================================
     // Mensagem de sistema: informa que conversa foi transferida
     // Aparece centralizada no chat principal
     // ========================================
     const systemMessage = `Conversa transferida para ${toAgentName}`;
     
     const { data: sysMsg, error: sysMsgError } = await supabase
       .from('messages')
       .insert({
         conversation_id: conversationId,
         sender_type: 'system',
         sender_id: user.id,
         content: systemMessage,
         message_type: 'text',
         direction: 'outbound',
         status: 'sent',
         sent_at: new Date().toISOString(),
       })
       .select()
       .single();

     // Se for webchat, dispara broadcast para o widget do visitante
     if (!sysMsgError && sysMsg && webchatData?.inboxId) {
       await supabase.functions.invoke('send-message', {
         body: {
           inbox_id: webchatData.inboxId,
           phone: '',
           content: systemMessage,
           message_type: 'text',
           message_id: sysMsg.id,
           conversation_id: conversationId,
         },
       });
     }

      toast.success('Conversa transferida com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro ao transferir conversa:', error);
      toast.error('Erro ao transferir conversa');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Aceita uma transferência pendente
   */
  const acceptTransfer = useCallback(async (transferId: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('conversation_transfers')
        .update({ 
          status: 'accepted',
          responded_at: new Date().toISOString(),
        })
        .eq('id', transferId);

      if (error) throw error;

      toast.success('Transferência aceita!');
      return true;
    } catch (error) {
      console.error('Erro ao aceitar transferência:', error);
      toast.error('Erro ao aceitar transferência');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Rejeita uma transferência - volta conversa para agente original
   */
  const rejectTransfer = useCallback(async (
    transferId: string,
    conversationId: string,
    originalAgentId: string | null
  ): Promise<boolean> => {
    setLoading(true);
    try {
      // 1. Atualiza status da transferência
      const { error: transferError } = await supabase
        .from('conversation_transfers')
        .update({ 
          status: 'rejected',
          responded_at: new Date().toISOString(),
        })
        .eq('id', transferId);

      if (transferError) throw transferError;

      // 2. Volta assigned_to para o agente original
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ assigned_to: originalAgentId })
        .eq('id', conversationId);

      if (updateError) throw updateError;

      toast.success('Transferência rejeitada');
      return true;
    } catch (error) {
      console.error('Erro ao rejeitar transferência:', error);
      toast.error('Erro ao rejeitar transferência');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca histórico de transferências de uma conversa
   */
  const fetchTransferHistory = useCallback(async (conversationId: string): Promise<ConversationTransfer[]> => {
    try {
      // Busca transferências
      const { data: transfers, error } = await supabase
        .from('conversation_transfers')
        .select(`
          id,
          conversation_id,
          from_agent_id,
          to_agent_id,
          from_inbox_id,
          to_inbox_id,
          note,
          status,
          created_at,
          responded_at
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Coleta IDs de agentes para buscar profiles
      const agentIds = new Set<string>();
      (transfers || []).forEach(t => {
        if (t.from_agent_id) agentIds.add(t.from_agent_id);
        if (t.to_agent_id) agentIds.add(t.to_agent_id);
      });

      // Busca profiles se houver agentes
      let profilesMap: Record<string, { full_name: string | null; avatar_url: string | null }> = {};
      if (agentIds.size > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', Array.from(agentIds));
        
        (profiles || []).forEach(p => {
          profilesMap[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url };
        });
      }

      return (transfers || []).map(t => ({
        ...t,
        from_agent: t.from_agent_id ? profilesMap[t.from_agent_id] : undefined,
        to_agent: t.to_agent_id ? profilesMap[t.to_agent_id] : undefined,
      }));
    } catch (error) {
      console.error('Erro ao buscar histórico de transferências:', error);
      return [];
    }
  }, []);

  /**
   * Busca transferências pendentes para o usuário atual
   */
  const fetchPendingTransfers = useCallback(async (): Promise<ConversationTransfer[]> => {
    if (!user?.id) return [];

    try {
      // Busca transferências pendentes
      const { data: transfers, error } = await supabase
        .from('conversation_transfers')
        .select(`
          id,
          conversation_id,
          from_agent_id,
          to_agent_id,
          from_inbox_id,
          to_inbox_id,
          note,
          status,
          created_at,
          responded_at
        `)
        .eq('to_agent_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Coleta IDs de agentes para buscar profiles
      const agentIds = new Set<string>();
      (transfers || []).forEach(t => {
        if (t.from_agent_id) agentIds.add(t.from_agent_id);
        if (t.to_agent_id) agentIds.add(t.to_agent_id);
      });

      // Busca profiles se houver agentes
      let profilesMap: Record<string, { full_name: string | null; avatar_url: string | null }> = {};
      if (agentIds.size > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', Array.from(agentIds));
        
        (profiles || []).forEach(p => {
          profilesMap[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url };
        });
      }

      return (transfers || []).map(t => ({
        ...t,
        from_agent: t.from_agent_id ? profilesMap[t.from_agent_id] : undefined,
        to_agent: t.to_agent_id ? profilesMap[t.to_agent_id] : undefined,
      }));
    } catch (error) {
      console.error('Erro ao buscar transferências pendentes:', error);
      return [];
    }
  }, [user?.id]);

  return {
    loading,
    agents,
    fetchAvailableAgents,
    transferToAgent,
    acceptTransfer,
    rejectTransfer,
    fetchTransferHistory,
    fetchPendingTransfers,
  };
}
